import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  CreditCard, Check, Crown, Building2, FileText, 
  Loader2, AlertCircle, Copy, Download
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [mySubscription, setMySubscription] = useState(null);
  const [myInvoices, setMyInvoices] = useState([]);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [bankAccount, setBankAccount] = useState(null);
  const [subscribing, setSubscribing] = useState(false);

  const featureLabels = {
    csv_export: "CSVエクスポート",
    print: "印刷機能",
    email: "メール通知",
    api: "API連携",
    multi_admin: "複数管理者"
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [plansRes, subRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/subscription/plans`, { headers }),
        axios.get(`${API}/subscription/my`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/subscription/invoices`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setPlans(plansRes.data || []);
      setMySubscription(subRes.data);
      setMyInvoices(invoicesRes.data || []);
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToPlan = async (planId, billingCycle = "monthly") => {
    setSubscribing(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/subscription/subscribe`, 
        { plan_id: planId, billing_cycle: billingCycle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      
      if (res.data.invoice_id) {
        // Show invoice details
        viewInvoice(res.data.invoice_id);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "プランの申し込みに失敗しました");
    } finally {
      setSubscribing(false);
    }
  };

  const viewInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/subscription/invoice/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedInvoice(res.data.invoice);
      setBankAccount(res.data.bank_account);
      setShowInvoiceDialog(true);
    } catch (error) {
      toast.error("請求書の取得に失敗しました");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("コピーしました");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-emerald-600">有効</Badge>;
      case "trial":
        return <Badge className="bg-blue-600">トライアル</Badge>;
      case "pending_payment":
        return <Badge className="bg-yellow-600">支払い待ち</Badge>;
      case "expired":
        return <Badge className="bg-red-600">期限切れ</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">サブスクリプション</h1>
        <p className="text-slate-400 text-sm">プランの選択・管理</p>
      </div>

      {/* Current Subscription */}
      {mySubscription && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              現在のプラン
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xl font-semibold">{mySubscription.plan?.name || "不明"}</p>
                <div className="flex items-center gap-2 mt-1">
                  {getStatusBadge(mySubscription.status)}
                  <span className="text-sm text-slate-400">
                    {mySubscription.billing_cycle === "yearly" ? "年払い" : "月払い"}
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-2">
                  有効期限: {new Date(mySubscription.current_period_end).toLocaleDateString('ja-JP')}
                </p>
              </div>
              {mySubscription.status === "pending_payment" && (
                <Button onClick={() => {
                  const pendingInvoice = myInvoices.find(i => i.status === "pending");
                  if (pendingInvoice) viewInvoice(pendingInvoice.id);
                }} className="bg-yellow-600 hover:bg-yellow-700">
                  <CreditCard className="w-4 h-4 mr-2" />
                  お支払い
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans */}
      <div>
        <h2 className="text-lg font-semibold mb-4">プラン一覧</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const isCurrentPlan = mySubscription?.plan_id === plan.id;
            
            return (
              <Card key={plan.id} className={`bg-slate-800 border-slate-700 ${isCurrentPlan ? 'ring-2 ring-emerald-500' : ''}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    {plan.name}
                    {isCurrentPlan && <Badge className="bg-emerald-600">現在のプラン</Badge>}
                  </CardTitle>
                  <CardDescription>
                    <span className="text-3xl font-bold text-white">¥{plan.price.toLocaleString()}</span>
                    <span className="text-slate-400">/月</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>従業員 {plan.employee_limit}名まで</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>QRコード打刻</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>勤怠記録管理</span>
                    </div>
                    {plan.features?.map((f) => (
                      <div key={f} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-emerald-500" />
                        <span>{featureLabels[f] || f}</span>
                      </div>
                    ))}
                  </div>
                  
                  {!isCurrentPlan && (
                    <Button 
                      onClick={() => subscribeToPlan(plan.id)}
                      disabled={subscribing}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : "このプランを選択"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {plans.length === 0 && (
          <div className="text-center text-slate-400 py-8">
            <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>利用可能なプランがありません</p>
          </div>
        )}
      </div>

      {/* My Invoices */}
      {myInvoices.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4">請求履歴</h2>
          <div className="space-y-3">
            {myInvoices.map((invoice) => (
              <Card key={invoice.id} className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        <span className="font-mono text-sm">{invoice.invoice_number}</span>
                        <Badge variant={invoice.status === "paid" ? "default" : "secondary"}>
                          {invoice.status === "paid" ? "支払済" : "未払い"}
                        </Badge>
                      </div>
                      <p className="text-lg font-semibold mt-1">¥{invoice.amount.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">
                        {invoice.status === "paid" 
                          ? `支払日: ${new Date(invoice.paid_at).toLocaleDateString('ja-JP')}`
                          : `期限: ${new Date(invoice.due_date).toLocaleDateString('ja-JP')}`
                        }
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => viewInvoice(invoice.id)}>
                      詳細
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              請求書詳細
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-700 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-slate-400">請求書番号</p>
                    <p className="font-mono">{selectedInvoice.invoice_number}</p>
                  </div>
                  <Badge variant={selectedInvoice.status === "paid" ? "default" : "secondary"}>
                    {selectedInvoice.status === "paid" ? "支払済" : "未払い"}
                  </Badge>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-slate-400">金額</p>
                  <p className="text-2xl font-bold">¥{selectedInvoice.amount.toLocaleString()}</p>
                </div>
                <div className="mt-2">
                  <p className="text-sm text-slate-400">プラン</p>
                  <p>{selectedInvoice.plan_name} ({selectedInvoice.billing_cycle === "yearly" ? "年払い" : "月払い"})</p>
                </div>
                <div className="mt-2">
                  <p className="text-sm text-slate-400">支払期限</p>
                  <p>{new Date(selectedInvoice.due_date).toLocaleDateString('ja-JP')}</p>
                </div>
              </div>

              {selectedInvoice.status !== "paid" && bankAccount && (
                <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-5 h-5 text-blue-400" />
                    <h3 className="font-semibold">振込先口座</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">銀行名</span>
                      <span>{bankAccount.bank_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">支店名</span>
                      <span>{bankAccount.branch_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">口座種別</span>
                      <span>{bankAccount.account_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">口座番号</span>
                      <div className="flex items-center gap-2">
                        <span>{bankAccount.account_number}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2"
                          onClick={() => copyToClipboard(bankAccount.account_number)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">口座名義</span>
                      <span>{bankAccount.account_holder}</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-700 rounded text-sm">
                    <p className="text-yellow-400">
                      ※ 振込名義人は請求書番号を含めてください<br />
                      例: {selectedInvoice.invoice_number.split('-').slice(1, 3).join('')} ヤマダタロウ
                    </p>
                  </div>
                </div>
              )}

              <Button 
                variant="outline" 
                onClick={() => setShowInvoiceDialog(false)} 
                className="w-full"
              >
                閉じる
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionPage;
